/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ColumnFiltersState,
  ColumnSort,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Table,
  useReactTable,
} from "@tanstack/react-table";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { DataTableProps } from "./DataTable";

const deepMerge = (obj1: any, obj2: any): any => {
  const output = Object.assign({}, obj1);

  for (const key in obj2) {
    if (Object.prototype.hasOwnProperty.call(obj2, key)) {
      if (typeof obj2[key] === "object" && obj2[key] !== null && obj1[key]) {
        output[key] = deepMerge(obj1[key], obj2[key]);
      } else {
        output[key] = obj2[key];
      }
    }
  }

  return output;
};

export interface IDataGridContextProps<TData extends object> {
  props: DataTableProps<TData>;
  table: Table<TData>;
  totalRows: number;
  loading: boolean;
  setLoading: (state: boolean) => void;
  selectedRows: TData[];
  toggleRowSelection: (id: TData) => void;
  toggleAllRowsSelection: (checked: boolean) => void;
  isSelectAllChecked: boolean;
  isSelectAllIndeterminate: boolean;
}

const DataGridContext = createContext<IDataGridContextProps<any> | undefined>(
  undefined,
);

export function useDataGrid() {
  const context = useContext(DataGridContext);
  if (!context) {
    throw new Error("useDataGrid must be used within a DataGridProvider");
  }
  return context;
}

interface DataGridProviderProps<TData extends object>
  extends DataTableProps<TData> {
  children?: React.ReactNode;
}

export const DataGridProvider = <TData extends object>({
  children,
  ...props
}: DataGridProviderProps<TData>) => {
  const defaultValues: Partial<DataTableProps<TData>> = {
    messages: {
      empty: "No data available",
      loading: "Loading...",
    },
    pagination: {
      info: "{from} - {to} of {count}",
      sizes: [5, 10, 25, 50, 100],
      sizesLabel: "Show",
      sizesDescription: "per page",
      size: 10,
      page: 0,
      moreLimit: 5,
      more: false,
    },
    defaultSelectedRows: [],
    layout: { cellsBorder: true },
    rowSelect: false,
  };

  const mergedProps = deepMerge(defaultValues, props);

  const data = useMemo(() => mergedProps.data ?? [], [mergedProps.data]);
  const [loading, setLoading] = useState(mergedProps.loading ?? false);
  const totalRows = mergedProps.data?.length;

  // State management for selected rows
  const [selectedRows, setSelectedRows] = useState<TData[]>([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [isSelectAllIndeterminate, setIsSelectAllIndeterminate] =
    useState<boolean>(false);
  // Pagination and Sorting from props
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: props.pagination?.page ?? 0,
    pageSize: props.enablePagination
      ? (props.pagination?.size ?? 10)
      : data.length,
  });
  const [sorting, setSorting] = useState<ColumnSort[]>(
    mergedProps.sorting ?? [],
  );
  const [filters, setFilters] = useState<ColumnFiltersState>(
    mergedProps.filters ?? [],
  );

  const table = useReactTable({
    columns: mergedProps.columns, // Access columns from mergedProps
    data: data, // Access data from mergedProps
    debugTable: false,
    pageCount: undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
    manualSorting: false,
    manualFiltering: false,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    state: {
      pagination,
      sorting,
      columnFilters: filters,
    },
    defaultColumn: {
      size: -1,
      minSize: -1, //enforced during column resizing
      maxSize: 500, //enforced during column resizing
    },
  });

  const toggleRowSelection = (id: TData) => {
    setSelectedRows((prevSelected) => {
      const newSelected: TData[] = [...prevSelected];
      if (newSelected.find((row) => row === id)) {
        newSelected.splice(newSelected.indexOf(id), 1);
      } else {
        newSelected.push(id);
      }
      if (props.onRowsSelectChange) {
        props.onRowsSelectChange(newSelected);
      }
      return newSelected;
    });
  };

  useEffect(() => {
    if (props.defaultSelectedRows) {
      setSelectedRows(props.defaultSelectedRows ?? []);
    }
  }, [props.defaultSelectedRows]);

  const toggleAllRowsSelection = (checked: boolean) => {
    const allRowIds = table
      .getRowModel()
      .rows.map((row) => row.original) as TData[];
    const newSelectedRows: TData[] = checked ? allRowIds : [];
    setSelectedRows(newSelectedRows);
    if (props.onRowsSelectChange) {
      props.onRowsSelectChange(newSelectedRows);
    }
  };

  // Handle sorting, pagination, and search loading
  const handleStateChange = () => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false); // Stop loading after action completes
    }, 300); // Delay for smooth experience
    return () => clearTimeout(timer);
  };

  // Listen for sorting changes
  useEffect(() => {
    if (table.getState().sorting.length > 0) {
      handleStateChange();
    }
  }, [table.getState().sorting]);

  // Listen for pagination changes
  useEffect(() => {
    handleStateChange();
  }, [table.getState().pagination]);

  // Handle data loading (trigger loading when data is being fetched)
  useEffect(() => {
    if (data.length === 0) {
      setLoading(true);
    } else {
      setLoading(false); // Data loaded
    }
  }, [data]);

  useEffect(() => {
    const allRowIds = table
      .getRowModel()
      .rows.map((row) => row.original) as TData[];
    const isAllSelected = selectedRows.length === allRowIds.length;
    const isSomeSelected = !isAllSelected && selectedRows.length > 0;

    setIsSelectAllChecked(isAllSelected);
    setIsSelectAllIndeterminate(!isAllSelected && isSomeSelected);
  }, [selectedRows, table.getRowModel().rows]);

  return (
    <DataGridContext.Provider
      value={{
        props: mergedProps,
        table,
        totalRows,
        loading,
        setLoading,
        selectedRows,
        toggleRowSelection,
        toggleAllRowsSelection,
        isSelectAllChecked,
        isSelectAllIndeterminate,
      }}
    >
      {children}
    </DataGridContext.Provider>
  );
};
